import { createSelector } from 'reselect';
import get from 'lodash/get';
import { ADD_PATIENT_STEPS, UI_POPUP_TYPES } from '../../constants/props';
import { selectIsVerifiedDependentRole } from '../context/selectors';
import {
  selectRegistrationCompleted,
  selectRequestPasswordUpdate,
} from '../auth/selectors';
import { selectUiPopupContentType } from '../ui/selectors';

const selectProfileState = state => get(state, 'profiles');

export const getUserSelector = state => state.profiles.getUser;

export const addPatientByProviderSelector = state =>
  state.profiles.addPatientByProvider;

export const selectCreatePatientPending = createSelector(
  selectProfileState,
  state => get(state, 'createPatient.sending'),
);

/** *************************** PROFILE EDITABLE ************************** * */

export const selectProfileEditable = createSelector(
  selectProfileState,
  state => get(state, 'profileEditable.fieldsEditable'),
);

/** *************************** EDIT USER ********************************** * */

export const selectEditedUser = createSelector(
  selectProfileState,
  state => get(state, 'editUser'),
);

export const selectEditedUserErrors = createSelector(
  selectEditedUser,
  state => get(state, 'error'),
);

export const selectEditedUserSending = createSelector(
  selectEditedUser,
  state => get(state, 'sending'),
);

export const selectEditedUserSucceed = createSelector(
  selectEditedUser,
  state => get(state, 'succeed'),
);

/** *************************** ACCESS LEVEL ******************************* * */

export const accessLevelSelector = state => state.profiles.getAccessLevels.list;

export const selectAccessLevelResponse = createSelector(
  selectProfileState,
  state => get(state, 'getAccessLevels'),
);

export const selectAccessLevelLoading = createSelector(
  selectAccessLevelResponse,
  state => get(state, 'loading', false),
);

/** ************************************************************************ * */

export const selectProfiles = state => state.profiles;

export const selectCreatedPatient = createSelector(
  selectProfiles,
  state => get(state, 'addPatientByProvider', {}),
);

export const selectCreatedPatientProfile = createSelector(
  selectCreatedPatient,
  state => get(state, 'primaryPatientProfile', {}),
);

export const selectCreatedPatientProfileId = createSelector(
  selectCreatedPatientProfile,
  state => get(state, 'id'),
);

export const selectEditPatientErrors = createSelector(
  selectProfiles,
  state => get(state, 'editPatient.error', false),
);

export const selectEditPatientSuccess = createSelector(
  selectProfiles,
  state => get(state, 'editPatient.succeed', false),
);

export const selectEditPatientPending = createSelector(
  selectProfiles,
  state => get(state, 'editPatient.sending', false),
);

export const sendingSelector = createSelector(
  getUserSelector,
  request => get(request, 'sending') || false,
);

export const addPatientSendingSelector = createSelector(
  addPatientByProviderSelector,
  request => get(request, 'sending') || false,
);

export const addPatientErrorSelector = createSelector(
  addPatientByProviderSelector,
  request => get(request, 'error', null),
);

export const addPatientSuccessSelector = createSelector(
  addPatientByProviderSelector,
  request => get(request, 'succeed') || false,
);

export const selectPatientProfileAlreadyExists = createSelector(
  addPatientByProviderSelector,
  request => get(request, 'meta.patientProfileAlreadyExists') || true,
);

export const canAssignProviderSelector = createSelector(
  accessLevelSelector,
  selectIsVerifiedDependentRole,
  (levels, isVerifiedDependent) => {
    return (
      (levels &&
        levels.some(item =>
          ['patient_caregiver', 'provider'].includes(item),
        )) ||
      isVerifiedDependent
    );
  },
);

export const selectIsAccessLeverProvider = createSelector(
  accessLevelSelector,
  levels => {
    return levels && levels.some(item => ['provider'].includes(item));
  },
);

export const selectIsAccessLeverPracticeProvider = createSelector(
  accessLevelSelector,
  levels => {
    return levels && levels.some(item => ['practice_provider'].includes(item));
  },
);

export const selectPublicProfileState = createSelector(
  selectProfileState,
  state => get(state, 'publicProfile'),
);
export const selectPublicProfile = createSelector(
  selectPublicProfileState,
  state => get(state, 'data'),
);

export const selectPublicProfileLoading = createSelector(
  selectPublicProfileState,
  state => get(state, 'sending'),
);

export const selectPublicProfileRejected = createSelector(
  selectPublicProfileState,
  state => get(state, 'rejected'),
);

export const selectUserDocuments = createSelector(
  selectProfileState,
  state => get(state, 'getUserDocuments', {}),
);

export const selectUserDocumentsList = createSelector(
  selectUserDocuments,
  state => get(state, 'list', []),
);

export const selectUserDocumentsLoading = createSelector(
  selectUserDocuments,
  state => get(state, 'sending', false),
);

export const selectDeleteUserDocuments = createSelector(
  selectProfileState,
  state => get(state, 'deleteUserDocuments', {}),
);

export const selectUploadUserDocuments = createSelector(
  selectProfileState,
  state => get(state, 'uploadUserDocuments', {}),
);

export const selectUpdateUserDocuments = createSelector(
  selectProfileState,
  state => get(state, 'updateUserDocuments', {}),
);

export const selectDeleteUserDocumentsLoading = createSelector(
  selectDeleteUserDocuments,
  state => get(state, 'sending', false),
);

export const selectUpdateUserDocumentsLoading = createSelector(
  selectUpdateUserDocuments,
  state => get(state, 'sending', false),
);

export const selectUploadUserDocumentsLoading = createSelector(
  selectUploadUserDocuments,
  state => get(state, 'sending', false),
);

export const selectUploadUserDocumentsSucceed = createSelector(
  selectUploadUserDocuments,
  state => get(state, 'succeed', false),
);
export const selectUploadUserDocumentsDataId = createSelector(
  selectUploadUserDocuments,
  state => get(state, 'id', {}),
);
export const selectUploadUserDocumentsDataKey = createSelector(
  selectUploadUserDocuments,
  state => get(state, 'key', ''),
);

export const selectGuardianDependents = createSelector(
  selectProfileState,
  state => get(state, 'getGuardianDependent', {}),
);

/** *************************** USER ATTACHMENTS ********************************** * */

export const selectUserAttachments = createSelector(
  selectProfileState,
  state => get(state, 'getUserAttachments', {}),
);

export const selectUserPaperConsents = createSelector(
  selectUserAttachments,
  state => get(state, 'paperConsents', []),
);

export const selectUserAttachmentsList = createSelector(
  selectUserAttachments,
  state => get(state, 'list', []),
);

export const selectUserSignedConsents = createSelector(
  selectUserAttachments,
  state => get(state, 'consents', []),
);

export const selectUserAttachmentsLoading = createSelector(
  selectUserAttachments,
  state => get(state, 'sending', false),
);

export const selectDeleteUserAttachment = createSelector(
  selectProfileState,
  state => get(state, 'deleteUserAttachment', {}),
);

export const selectDeleteUserAttachmentLoading = createSelector(
  selectDeleteUserAttachment,
  state => get(state, 'sending', false),
);

export const selectDeleteUserOutcomeAttachment = createSelector(
  selectProfileState,
  state => get(state, 'deleteUserOutcomeAttachment', {}),
);

export const selectDeleteUserOutcomeAttachmentLoading = createSelector(
  selectDeleteUserOutcomeAttachment,
  state => get(state, 'sending', false),
);

/** ****************************************************************************** * */

export const selectDocumentsPageLoading = createSelector(
  selectUserDocumentsLoading,
  selectUploadUserDocumentsLoading,
  selectDeleteUserDocumentsLoading,
  selectUpdateUserDocumentsLoading,
  selectUserAttachmentsLoading,
  selectDeleteUserAttachmentLoading,
  selectDeleteUserOutcomeAttachmentLoading,
  (
    documentsLoading,
    documentsUpload,
    documentDelete,
    documentUpdate,
    attachmentsLoading,
    attachmentDelete,
  ) =>
    documentDelete ||
    documentUpdate ||
    documentsUpload ||
    documentsLoading ||
    attachmentsLoading ||
    attachmentDelete,
);

export const selectIsLoadingGuardianDependents = createSelector(
  selectGuardianDependents,
  state => get(state, 'sending', false),
);

export const selectGuardianDependentsList = createSelector(
  selectGuardianDependents,
  state => get(state, 'list', []),
);

export const selectVerifiedGuardianDependentsList = createSelector(
  selectGuardianDependentsList,
  list => list.filter(item => get(item, 'status') === 'verified'),
);

export const selectGuardianDependentById = id =>
  createSelector(
    selectGuardianDependentsList,
    list => list.find(item => item.id === id),
  );

export const selectDependentStatusById = id =>
  createSelector(
    selectGuardianDependentsList,
    list => {
      const dependent = list.find(item => item.dependent.id === id);
      if (dependent) return get(dependent, 'status');
    },
  );

export const selectIsSignedMainConsentSelector = state =>
  get(state, 'profiles.getLoggedUser.messagingConsentsAccepted');

export const selectMainConsent = state =>
  get(state, 'profiles.getMainConsent.list', null);

export const selectPatientContacts = createSelector(
  selectProfileState,
  state => get(state, 'addPatientData', {}),
);

export const selectPatientPhoneNumberCountryIsoCode = createSelector(
  selectProfileState,
  state => get(state, 'addPatientData.contacts.phoneNumberCountryIsoCode', ''),
);

export const selectSavedPatientContacts = createSelector(
  selectPatientContacts,
  state => get(state, ADD_PATIENT_STEPS.CONTACTS, null),
);

export const selectSavedPatientPersonalInfo = createSelector(
  selectPatientContacts,
  state => get(state, ADD_PATIENT_STEPS.PERSONAL_INFO, null),
);

export const selectSavedPatientAddress = createSelector(
  selectPatientContacts,
  state => get(state, ADD_PATIENT_STEPS.ADDRESS, null),
);

export const selectAddedPatientsList = createSelector(
  addPatientByProviderSelector,
  state => get(state, 'list', []),
);

export const selectCurrentCountryCode = state =>
  get(state, 'profiles.countryCode.currentCountryCode', '+1');

export const selectUserPhoneNumberCountryCode = state =>
  get(state, 'profiles.getUser.phoneNumberCountryCode', null);

export const selectUserPhoneNumberCountryISOCode = state =>
  get(state, 'profiles.getUser.phoneNumberCountryIsoCode', null);

export const selectUserPrimaryPatientProfile = state =>
  get(state, 'context.targetUser.primaryPatientProfile', null);

export const selectUserMetricForHeight = state =>
  get(
    state,
    'context.targetUser.settings.measurementUnitsSettings.height',
    'feet_and_inches',
  );

export const selectUserMetricForWeight = state =>
  get(
    state,
    'context.targetUser.settings.measurementUnitsSettings.weight',
    'pounds',
  );

export const selectSignupProcessPending = createSelector(
  selectCreatePatientPending,
  selectEditedUserSending,
  (createPatient, editUser) => createPatient || editUser,
);

export const getMfaConfirmationData = state =>
  get(state, 'profiles.mfaConfirmation', null);

export const getEnableMfa = state => get(state, 'profiles.enableMfa', null);

export const selectProviderProfile = createSelector(
  selectProfileState,
  state => get(state, 'getParentMember'),
);

export const selectProviderCalendarManageAccess = createSelector(
  selectProviderProfile,
  state => get(state, 'calendarManageable', false),
);

export const selectUser = createSelector(
  selectProfileState,
  state => get(state, 'getLoggedUser'),
);

export const selectShowMfaAfterMobileAppPopup = createSelector(
  selectUiPopupContentType,
  selectRequestPasswordUpdate,
  selectUser,
  selectRegistrationCompleted,
  (
    uiPopupContentType,
    isRequestPasswordUpdate,
    user,
    isRegistrationCompleted,
  ) => {
    const showMfaNotice = get(user, 'showMfaNotice', false);
    const mfaEnabled = get(user, 'mfaEnabled', false);

    return (
      showMfaNotice &&
      !mfaEnabled &&
      !isRequestPasswordUpdate &&
      isRegistrationCompleted &&
      uiPopupContentType === UI_POPUP_TYPES.MOBILE_APP
    );
  },
);

export const selectManualActivateInsuranceEligibilitySucceed = createSelector(
  selectProfileState,
  state => get(state, 'manualActivateInsuranceEligibility.succeed'),
);

export const selectEditPatientEmergencyContactSucceed = createSelector(
  selectProfileState,
  state => get(state, 'editPatientEmergencyContact.succeed', false),
);

export const selectEditPatientEmergencyContactPending = createSelector(
  selectProfileState,
  state => get(state, 'editPatientEmergencyContact.sending', false),
);

export const selectEditPatientEmergencyContactError = createSelector(
  selectProfileState,
  state => get(state, 'editPatientEmergencyContact.error', false),
);



// WEBPACK FOOTER //
// ./src/redux/profiles/selectors.js